import { useMutation, useQueryClient } from "@tanstack/react-query";
import { facilityPhotosKeys } from "Api/Queries/Merchants/useGetFacilityPhotosQuery";
import { merchantKeys } from "Api/Queries/Merchants/useGetMerchantQuery";
import {
  apiPost,
  getApiUrl,
  postMerchantsPublicIDFacilitiesFacilityPublicIDPhotosPath,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

type Parameters = {
  merchantPublicID: string;
  facilityPublicID: string;
  photoOutside?: File | null;
  photoInside?: File | null;
};

type Props = {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
};

export const useUploadFacilityPhotosMutation = ({
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();

  // Explicit generics: the endpoint has no response body, so the wrapper's result type
  // collapses to `never` and React Query cannot infer the variables from it.
  return useMutation<void, unknown, Parameters>({
    // The wrapper turns 4xx/5xx/network failures into rejections — the generated
    // fetch helper itself never throws, which used to make a rejected photo look saved.
    mutationFn: processResponseWithApiProblemDetails(
      async ({
        merchantPublicID,
        facilityPublicID,
        photoOutside,
        photoInside,
      }: Parameters) => {
        const form = new FormData();
        if (photoOutside) {
          form.append("PhotoOutside", photoOutside);
        }
        if (photoInside) {
          form.append("PhotoInside", photoInside);
        }

        return apiPost(
          `${getApiUrl()}${postMerchantsPublicIDFacilitiesFacilityPublicIDPhotosPath(merchantPublicID, facilityPublicID)}`,
          form,
          {},
        );
      },
    ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: merchantKeys.all });
      queryClient.invalidateQueries({
        queryKey: facilityPhotosKeys.byIds(
          variables.merchantPublicID,
          variables.facilityPublicID,
        ),
      });
      onSuccess?.();
    },
    onError,
  });
};
